import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Download, ShieldCheck, RotateCcw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = {
  active: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" },
  pending_payment: { label: "Menunggu Pembayaran", cls: "bg-amber-50 text-amber-700 hover:bg-amber-50" },
  expired: { label: "Habis — Terkunci", cls: "bg-rose-50 text-rose-700 hover:bg-rose-50" },
  none: { label: "Tanpa Langganan", cls: "bg-slate-100 text-slate-600 hover:bg-slate-100" },
};

function subStatus(s) {
  const sub = s.subscription;
  if (!sub || !sub.valid_until) return "none";
  if (sub.status === "pending_payment") return "pending_payment";
  return new Date(sub.valid_until) < new Date() ? "expired" : "active";
}

function daysLeft(s) {
  const vu = s.subscription?.valid_until;
  if (!vu) return null;
  return Math.ceil((new Date(vu).getTime() - Date.now()) / 86400000);
}

export default function Subscriptions() {
  const [schools, setSchools] = useState(null);
  const [plans, setPlans] = useState([]);
  const [backups, setBackups] = useState([]);
  const [adminContact, setAdminContact] = useState(null);
  const [target, setTarget] = useState(null);
  const [plan, setPlan] = useState("3bulan");
  const [saving, setSaving] = useState(false);
  const [subPays, setSubPays] = useState([]);
  const [subRejected, setSubRejected] = useState([]);
  const [subReject, setSubReject] = useState(null);
  const [subReason, setSubReason] = useState("");
  const [subProcessing, setSubProcessing] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState(null);
  const [restoreSchools, setRestoreSchools] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState("");
  const [restoring, setRestoring] = useState(false);

  const load = () => {
    api.get("/schools").then((r) => setSchools(r.data)).catch((e) => { setSchools([]); toast.error(apiError(e)); });
    api.get("/auth/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/backups").then((r) => setBackups(r.data)).catch(() => {});
    api.get("/public/admin-contact").then((r) => setAdminContact(r.data)).catch(() => {});
    api.get("/subscription-payments?status=pending").then((r) => setSubPays(r.data)).catch(() => {});
    api.get("/subscription-payments?status=rejected").then((r) => setSubRejected(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const handleExtend = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/schools/${target.id}/subscription`, { plan });
      toast.success(`Langganan ${target.name} diperbarui`);
      setTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const approveSubPay = async (p) => {
    setSubProcessing(true);
    try {
      const r = await api.post(`/subscription-payments/${p.id}/approve`);
      toast.success(`${p.school_name} aktif sampai ${r.data.subscription.valid_until.slice(0, 10)}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubProcessing(false);
    }
  };

  const rejectSubPay = async (e) => {
    e.preventDefault();
    setSubProcessing(true);
    try {
      await api.post(`/subscription-payments/${subReject.id}/reject`, { reason: subReason });
      toast.success("Bukti pembayaran ditolak");
      setSubReject(null);
      setSubReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubProcessing(false);
    }
  };

  const openRestore = async (b) => {
    setRestoreBackup(b);
    setRestoreTarget("");
    setRestoreSchools([]);
    try {
      const r = await api.get(`/backups/${b.id}/schools`);
      setRestoreSchools(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doRestore = async (e) => {
    e.preventDefault();
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const r = await api.post("/backups/restore", { backup_id: restoreBackup.id, school_id: restoreTarget });
      toast.success(r.data.message);
      setRestoreBackup(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Langganan Sekolah</h1>
        <p className="mt-1 text-sm text-slate-500">Pantau paket dan sisa waktu langganan tiap sekolah.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-[#E6F0F9] p-4 text-sm leading-relaxed text-slate-600" data-testid="subscription-policy-note">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0060AF]" strokeWidth={1.5} />
          <span><b>Kebijakan:</b> sekolah yang belum membayar atau langganannya habis otomatis <b>terkunci</b> (staf & orang tua tidak bisa masuk), tetapi <b>data tidak dihapus</b>. Klik <b>Aktifkan / Perpanjang</b> setelah pembayaran diterima untuk membuka kembali aksesnya. Bendahara sekolah mendapat pengingat WhatsApp otomatis mulai H-7 sebelum berakhir.
          {adminContact?.phone && <span className="mt-1 block" data-testid="subscription-admin-contact">Kontak admin: <b>{adminContact.name}</b> · WA <b>{adminContact.phone}</b>{adminContact.email ? ` · ${adminContact.email}` : ""}</span>}
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="overflow-x-auto">
          <Table data-testid="subscriptions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Sekolah</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Berlaku Sampai</TableHead>
                <TableHead className="text-right">Sisa Hari</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools === null && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {schools !== null && schools.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Belum ada sekolah.</TableCell></TableRow>
              )}
              {(schools || []).map((s) => {
                const st = subStatus(s);
                const dl = daysLeft(s);
                return (
                  <TableRow key={s.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`subscription-row-${s.id}`}>
                    <TableCell className="text-sm font-medium text-slate-800">{s.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.subscription?.label || "-"}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full ${STATUS[st].cls}`} data-testid={`subscription-status-${s.id}`}>{STATUS[st].label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{s.subscription?.valid_until ? s.subscription.valid_until.slice(0, 10) : "-"}</TableCell>
                    <TableCell className={`text-right text-sm font-medium ${dl !== null && dl <= 7 ? "text-rose-600" : "text-slate-700"}`}>
                      {dl === null ? "-" : dl < 0 ? `Lewat ${-dl} hari` : `${dl} hari`}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => { setTarget(s); setPlan(s.subscription?.plan && plans.some((p) => p.id === s.subscription.plan) ? s.subscription.plan : "3bulan"); }}
                              data-testid={`extend-button-${s.id}`} className="gap-1 border-[#0060AF] text-[#0060AF] hover:bg-[#E5F0FA]">
                        <CreditCard className="h-3.5 w-3.5" strokeWidth={1.5} /> {st === "active" ? "Perpanjang" : "Aktifkan"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-6" data-testid="subscription-payments-section">
        <h3 className="font-heading text-base font-semibold text-slate-800">Bukti Pembayaran Langganan Masuk</h3>
        <p className="mt-1 text-xs text-slate-500">Setujui untuk mengaktifkan langganan sekolah (WA konfirmasi otomatis ke bendahara), atau tolak dengan alasan.</p>
        <div className="mt-3 space-y-2">
          {subPays.length === 0 && <p className="text-sm text-slate-400" data-testid="subscription-payments-empty">Tidak ada bukti pembayaran menunggu konfirmasi.</p>}
          {subPays.map((p) => (            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-100 bg-white p-3" data-testid={`subpay-row-${p.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{p.school_name}</p>
                <p className="text-xs text-slate-500">{p.email} · {new Date(p.created_at).toLocaleString("id-ID")}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.proof_path}?auth=${localStorage.getItem("sb_token")}`} target="_blank" rel="noreferrer"
                   data-testid={`subpay-proof-${p.id}`} className="text-sm font-medium text-[#0060AF] hover:underline">Lihat Bukti</a>
                <Button size="sm" onClick={() => approveSubPay(p)} disabled={subProcessing} data-testid={`approve-subpay-${p.id}`} className="bg-emerald-600 text-white hover:bg-emerald-700">Setujui</Button>
                <Button size="sm" variant="outline" onClick={() => { setSubReject(p); setSubReason(""); }} data-testid={`reject-subpay-${p.id}`} className="border-rose-300 text-rose-600 hover:bg-rose-50">Tolak</Button>
              </div>
            </div>
          ))}
        </div>
        {subRejected.length > 0 && (
          <div className="mt-3 border-t border-amber-100 pt-3" data-testid="subscription-rejected-list">
            <p className="mb-2 text-xs font-medium text-slate-500">Ditolak sebelumnya:</p>
            {subRejected.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-500" data-testid={`subpay-rejected-${p.id}`}>
                <span>{p.school_name} · {p.email} · {new Date(p.created_at).toLocaleDateString("id-ID")}</span>
                <span className="text-rose-500">{p.reject_reason || "Ditolak"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="backups-section">
        <h3 className="font-heading text-base font-semibold text-slate-800">Cadangan Data Harian</h3>
        <p className="mt-1 text-xs text-slate-500">Seluruh data aplikasi dicadangkan otomatis 2x sehari (01.00 & 13.00 WIB) ke penyimpanan aman, plus cadangan pengaman otomatis setiap kali sekolah diarsipkan. Unduh untuk arsip pribadi Anda.</p>
        <div className="mt-3 space-y-2">
          {backups.length === 0 && <p className="text-sm text-slate-400" data-testid="backups-empty">Belum ada cadangan (dibuat otomatis tiap malam).</p>}
          {backups.length > 10 && <p className="text-xs text-slate-400" data-testid="backups-truncated">Menampilkan 10 cadangan terbaru dari {backups.length}.</p>}
          {backups.slice(0, 10).map((b) => (
            <div key={b.id || b.created_at} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`backup-row-${(b.id || b.date).replace(/[^a-zA-Z0-9-]/g, "")}`}>
              <span className="text-sm text-slate-700">{new Date(b.created_at).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {(b.size / 1024).toFixed(0)} KB · {b.schools_count} sekolah</span>
              <div className="flex items-center gap-3">
                <button onClick={() => openRestore(b)} data-testid={`backup-restore-${(b.id || b.date).replace(/[^a-zA-Z0-9-]/g, "")}`}
                        className="flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} /> Pulihkan
                </button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.path}?auth=${localStorage.getItem("sb_token")}`}
                   data-testid={`backup-download-${(b.id || b.date).replace(/[^a-zA-Z0-9-]/g, "")}`} className="flex items-center gap-1 text-sm font-medium text-[#0060AF] hover:underline">
                  <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> Unduh
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={() => setTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Aktifkan / Perpanjang — {target?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleExtend} className="space-y-4">
            <p className="text-sm text-slate-600">Jika langganan masih berlaku, paket baru <b>menambah</b> sisa waktu. Jika sudah habis/baru daftar, dihitung mulai hari ini.</p>
            <div className="space-y-2">
              <Label>Paket</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="extend-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`extend-plan-${p.id}`}>
                      {p.label} — Rp {p.price.toLocaleString("id-ID")} ({p.months} bulan)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTarget(null)} data-testid="extend-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="extend-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Memproses..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!subReject} onOpenChange={() => setSubReject(null)}>
        <DialogContent aria-describedby={undefined} data-testid="subpay-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tolak Bukti — {subReject?.school_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={rejectSubPay} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sub-reason">Alasan Penolakan</Label>
              <Input id="sub-reason" data-testid="subpay-reject-reason" value={subReason} onChange={(e) => setSubReason(e.target.value)} placeholder="Contoh: nominal tidak sesuai / bukti tidak terbaca" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubReject(null)} data-testid="subpay-reject-cancel">Batal</Button>
              <Button type="submit" disabled={subProcessing} data-testid="subpay-reject-submit" className="bg-rose-600 text-white hover:bg-rose-700">Tolak</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restoreBackup} onOpenChange={() => setRestoreBackup(null)}>
        <DialogContent aria-describedby={undefined} data-testid="backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Pulihkan dari Cadangan {restoreBackup?.date}</DialogTitle>
          </DialogHeader>
          <form onSubmit={doRestore} className="space-y-4">
            <p className="text-sm text-slate-600">Pilih sekolah di dalam file cadangan ini. Data yang sudah ada tidak ditimpa — hanya data yang hilang yang dikembalikan.</p>
            <div className="space-y-2">
              <Label>Sekolah</Label>
              <Select value={restoreTarget} onValueChange={setRestoreTarget}>
                <SelectTrigger data-testid="restore-school-select"><SelectValue placeholder="Pilih sekolah..." /></SelectTrigger>
                <SelectContent>
                  {restoreSchools.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`restore-school-option-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRestoreBackup(null)} data-testid="restore-cancel">Batal</Button>
              <Button type="submit" disabled={restoring || !restoreTarget} data-testid="restore-submit" className="bg-emerald-600 text-white hover:bg-emerald-700">
                {restoring ? "Memulihkan..." : "Pulihkan Data"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
